import graphql from "babel-plugin-relay/macro";
import { add } from "date-fns";
import { Suspense, useEffect, useState } from "react";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { ScheduleOption } from "@/components/ScheduleSwitcher";
import { useDepartmentMaxGolfCourseBookingWindow } from "@/features/Department/hooks/useDepartmentMaxGolfCourseBookingWindow";
import { useGetBookingWindowDaysByFacilityId } from "@/hooks/useGetBookingWindowDaysByFacilityId";
import { useGetClubStartOfWeekNumberPatron } from "@/hooks/useGetClubStartOfWeekNumberPatron";
import { useScheduleNavigator } from "@/hooks/useScheduleNavigator";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { CalendarBody } from "@whoosh/design-system/src/components/controls/CalendarBody/components/CalendarBody";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { dateUtils } from "@whoosh/utilities";
import { NumericalDayOfWeek } from "@whoosh/utilities/src/calendars";
import { useTodaysDate } from "@whoosh/utilities/src/dates";

import { BookNowCalendarForDepartmentQuery } from "./__generated__/BookNowCalendarForDepartmentQuery.graphql";
import { BookNowCalendarForFacilityQuery } from "./__generated__/BookNowCalendarForFacilityQuery.graphql";

type BookNowCalendarProps = {
  scheduleOption: ScheduleOption;
  onNavigate?: () => void;
  calendarOrientation: "horizontal" | "vertical";
  clubWeekStartsOn?: NumericalDayOfWeek;
  twoMonthView?: boolean;
};

export const BookNowCalendar = ({
  scheduleOption,
  onNavigate,
  calendarOrientation,
  twoMonthView,
}: BookNowCalendarProps) => {
  return scheduleOption?.type === "facility" ? (
    <BookNowCalendarForFacility
      scheduleOption={scheduleOption}
      onNavigate={onNavigate}
      calendarOrientation={calendarOrientation}
      twoMonthView={twoMonthView}
    />
  ) : (
    <BookNowCalendarForDepartment
      scheduleOption={scheduleOption}
      onNavigate={onNavigate}
      calendarOrientation={calendarOrientation}
      twoMonthView={twoMonthView}
    />
  );
};

type InnerProps = {
  scheduleOption: ScheduleOption;
  bookingWindowDays: number;
  onSelectDate: (date: string) => void;
  calendarOrientation: "horizontal" | "vertical";
  clubWeekStartsOn: NumericalDayOfWeek;
  twoMonthView?: boolean;
};

const BookNowCalendarInner = ({
  bookingWindowDays,
  scheduleOption,
  onSelectDate,
  calendarOrientation,
  clubWeekStartsOn,
  twoMonthView,
}: InnerProps) => {
  const today = useTodaysDate();
  const todayObj = dateUtils.convertDateStringToObj(today);
  const nextDateOut = add(todayObj, { days: bookingWindowDays });
  const nextDateOutString =
    dateUtils.convertDateObjToServerStringFormat(nextDateOut);

  /*
   * this is just so the CalendarBody is re-rendered whenever the facility is changed.
   * this stops it from being on a month in the future that might not be available
   * for booking on the new facility
   * i.e., tennis allows 90 days of booking and user goes 90 days out and switches to golf
   * which only allows 14 days of bookings.  The calendar would then show no availability
   */
  const [key, setKey] = useState(Date.now());
  useEffect(() => {
    setKey(Date.now());
  }, [scheduleOption]);

  return (
    <CalendarBody
      key={key}
      onSelectDate={onSelectDate}
      startDate={today}
      displayHorizontally={calendarOrientation === "horizontal"}
      endDate={nextDateOutString}
      clubWeekStartsOn={clubWeekStartsOn}
      twoMonthView={twoMonthView}
    />
  );
};

// FACILITY
const BookNowCalendarForFacility = ({
  scheduleOption,
  onNavigate,
  calendarOrientation,
  twoMonthView,
}: {
  scheduleOption: ScheduleOption;
  onNavigate?: () => void;
  calendarOrientation: "horizontal" | "vertical";
  twoMonthView?: boolean;
}) => {
  const [queryRef, loadQuery] = useQueryLoader<BookNowCalendarForFacilityQuery>(
    BookNowCalendarForFacilityQueryString
  );

  useEffect(() => {
    loadQuery({ facilityId: scheduleOption.id });
  }, [loadQuery, scheduleOption.id]);

  return (
    <Suspense fallback={<Box height={426} />}>
      {queryRef && (
        <BookNowCalendarForFacilityInner
          onNavigate={onNavigate}
          queryRef={queryRef}
          scheduleOption={scheduleOption}
          calendarOrientation={calendarOrientation}
          twoMonthView={twoMonthView}
        />
      )}
    </Suspense>
  );
};

const BookNowCalendarForFacilityInner = ({
  queryRef,
  scheduleOption,
  onNavigate,
  calendarOrientation,
  twoMonthView,
}: {
  queryRef: PreloadedQuery<BookNowCalendarForFacilityQuery>;
  scheduleOption: ScheduleOption;
  onNavigate?: () => void;
  calendarOrientation: "horizontal" | "vertical";
  twoMonthView?: boolean;
}) => {
  const trackEvent = useTrackEvent();

  const navigateToSchedule = useScheduleNavigator();

  const data = usePreloadedQuery(
    BookNowCalendarForFacilityQueryString,
    queryRef
  );
  const bookingWindowDays = useGetBookingWindowDaysByFacilityId({
    fragmentRef: data.node ?? null,
  });

  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(
    data.node?.club ?? null
  );

  const onSelectDate = (date: string) => {
    trackEvent("Homepage.DateSelected", {
      date: date,
      facilitySlug: scheduleOption.slug,
    });
    navigateToSchedule("facility", scheduleOption.slug, date);
    if (onNavigate) onNavigate();
  };

  return (
    <BookNowCalendarInner
      bookingWindowDays={bookingWindowDays}
      scheduleOption={scheduleOption}
      onSelectDate={onSelectDate}
      calendarOrientation={calendarOrientation}
      clubWeekStartsOn={clubWeekStartsOn}
      twoMonthView={twoMonthView}
    />
  );
};

export const BookNowCalendarForFacilityQueryString = graphql`
  query BookNowCalendarForFacilityQuery($facilityId: ID!) {
    node(id: $facilityId) {
      ... on Facility {
        ...useGetBookingWindowDaysByFacilityId
        club {
          ...useGetClubStartOfWeekNumberPatron_club
        }
      }
    }
  }
`;

// DEPARTMENT
const BookNowCalendarForDepartment = ({
  scheduleOption,
  onNavigate,
  calendarOrientation,
  twoMonthView,
}: {
  scheduleOption: ScheduleOption;
  onNavigate?: () => void;
  calendarOrientation: "horizontal" | "vertical";
  twoMonthView?: boolean;
}) => {
  const [queryRef, loadQuery] =
    useQueryLoader<BookNowCalendarForDepartmentQuery>(
      BookNowCalendarForDepartmentQueryString
    );

  useEffect(() => {
    loadQuery({ departmentId: scheduleOption.id });
  }, [loadQuery, scheduleOption.id]);

  return (
    <Suspense fallback={<Box height={426} />}>
      {queryRef && (
        <BookNowCalendarForDepartmentInner
          queryRef={queryRef}
          scheduleOption={scheduleOption}
          onNavigate={onNavigate}
          calendarOrientation={calendarOrientation}
          twoMonthView={twoMonthView}
        />
      )}
    </Suspense>
  );
};

const BookNowCalendarForDepartmentInner = ({
  queryRef,
  scheduleOption,
  onNavigate,
  calendarOrientation,
  twoMonthView,
}: {
  queryRef: PreloadedQuery<BookNowCalendarForDepartmentQuery>;
  scheduleOption: ScheduleOption;
  onNavigate?: () => void;
  calendarOrientation: "horizontal" | "vertical";
  twoMonthView?: boolean;
}) => {
  const navigateToSchedule = useScheduleNavigator();

  const data = usePreloadedQuery(
    BookNowCalendarForDepartmentQueryString,
    queryRef
  );

  const bookingWindowDays = useDepartmentMaxGolfCourseBookingWindow(
    data.node ?? null
  );

  const clubWeekStartsOn = useGetClubStartOfWeekNumberPatron(
    data.node?.club ?? null
  );

  const onSelectDate = (date: string) => {
    navigateToSchedule("department", scheduleOption.slug, date);
    if (onNavigate) onNavigate();
  };

  return (
    <BookNowCalendarInner
      bookingWindowDays={bookingWindowDays}
      scheduleOption={scheduleOption}
      onSelectDate={onSelectDate}
      calendarOrientation={calendarOrientation}
      clubWeekStartsOn={clubWeekStartsOn}
      twoMonthView={twoMonthView}
    />
  );
};

export const BookNowCalendarForDepartmentQueryString = graphql`
  query BookNowCalendarForDepartmentQuery($departmentId: ID!) {
    node(id: $departmentId) {
      ... on Department {
        ...useDepartmentMaxGolfCourseBookingWindow_department
        club {
          ...useGetClubStartOfWeekNumberPatron_club
        }
      }
    }
  }
`;
